import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import AdminLayout from '../../components/AdminLayout';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  ExternalLink,
  Pencil,
  BarChart3,
  Trash2,
  Plus,
  ChevronRight,
  ChevronLeft,
  Copy
} from 'lucide-react';

const PAGE_SIZE = 10;

function CampaignList() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    confirmButtonClass: ''
  });

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE)
      });
      if (search.trim()) params.set('search', search.trim());
      const response = await api.get(`/admin/campaigns?${params.toString()}`);
      setCampaigns(response.data.campaigns);
      setTotal(response.data.total ?? 0);
      setTotalPages(response.data.totalPages ?? 1);
      setError('');
    } catch (err) {
      console.error('Fetch campaigns error:', err);
      setError('Failed to load campaigns');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleStatusChange = async (campaignId, newStatus) => {
    try {
      await api.patch(`/admin/campaigns/${campaignId}/status`, { status: newStatus });
      fetchCampaigns(); // Refresh list
    } catch (err) {
      console.error('Update status error:', err);
      alert('Failed to update campaign status');
    }
  };

  const handleDuplicate = async (campaignId, campaignName) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Duplicate Campaign',
      message: `Duplicate campaign "${campaignName}"?\n\nThis will create a copy with all settings, themes, forms, products, and configurations.`,
      confirmButtonClass: 'bg-blue-400 hover:bg-blue-500',
      onConfirm: async () => {
        try {
          const response = await api.post(`/admin/campaigns/${campaignId}/duplicate`);
          const newCampaignId = response.data.campaign?.id;
          
          if (newCampaignId) {
            // Redirect to edit page of the new campaign
            navigate(`/admin/campaigns/${newCampaignId}/edit`);
          } else {
            alert('Campaign duplicated but unable to navigate to edit page');
            fetchCampaigns(); // Refresh list
          }
        } catch (err) {
          console.error('Duplicate error:', err);
          alert(err.response?.data?.error || 'Failed to duplicate campaign');
        }
      }
    });
  };

  const handleDelete = async (campaignId, campaignName) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    const hasEntries = campaign?.total_entries > 0;
    
    let message = `Are you sure you want to delete "${campaignName}"?`;
    if (hasEntries) {
      message += `\n\nWARNING: This campaign has ${campaign.total_entries} entries. All entries will be permanently deleted.`;
    }
    
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Campaign',
      message: message,
      confirmButtonClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: async () => {
        try {
          await api.delete(`/admin/campaigns/${campaignId}`);
          fetchCampaigns(); // Refresh list
        } catch (err) {
          console.error('Delete error:', err);
          alert(err.response?.data?.error || 'Failed to delete campaign');
        }
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '00/00/00';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <AdminLayout activeSection="CAMPAIGN">
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmButtonClass={confirmDialog.confirmButtonClass}
      />
      
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-white mb-6">CAMPAIGNS</h1>
      </div>

      {/* Filters - same UI as Indemnity Form Submissions */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <form onSubmit={handleSearchSubmit} className="flex gap-2">
              <div className="flex-1">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by name or slug..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                Search
              </button>
            </form>
          </div>

          {/* Clear filter */}
          <div className="flex items-end">
            {search ? (
              <button
                type="button"
                onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                Clear filters
              </button>
            ) : null}
          </div>
        </div>

        {/* Stats - same as Indemnity "Showing X of Y submissions" */}
        <div className="mt-4 text-sm text-gray-600">
          {total === 0
            ? 'No campaigns'
            : `Showing ${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, total)} of ${total} campaigns`}
        </div>
      </div>

      {/* Error Message - same as Indemnity */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden" style={{ minHeight: '600px' }}>
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-teal-800 text-white">
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  CAMPAIGN
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                  STATUS
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                  START DATE
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                  END DATE
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                  ENTRIES
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {campaigns.map((campaign) => (
                <tr 
                  key={campaign.id} 
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/admin/campaigns/${campaign.id}/edit`)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                    <div className="text-sm text-gray-500">{campaign.mechanics_type.replace(/_/g, ' ')}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <select
                      value={campaign.status}
                      onChange={(e) => handleStatusChange(campaign.id, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className={`px-3 py-1 text-xs font-semibold rounded-full border-0 focus:ring-2 focus:ring-teal-500 ${
                        campaign.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : campaign.status === 'paused'
                          ? 'bg-yellow-100 text-yellow-800'
                          : campaign.status === 'completed'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="completed">Completed</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                    {formatDate(campaign.start_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                    {formatDate(campaign.end_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                    {campaign.total_entries || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <a
                        href={`/campaign/${campaign.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                        title="View Public Campaign"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-5 h-5" />
                      </a>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/campaigns/${campaign.id}/edit`);
                        }}
                        className="text-teal-600 hover:text-teal-800"
                        title="Edit Campaign"
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicate(campaign.id, campaign.name);
                        }}
                        className="text-purple-600 hover:text-purple-800"
                        title="Duplicate Campaign"
                      >
                        <Copy className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/campaigns/${campaign.id}/entries`);
                        }}
                        className="text-indigo-600 hover:text-indigo-800"
                        title="View Entries"
                      >
                        <BarChart3 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(campaign.id, campaign.name);
                        }}
                        className="text-red-600 hover:text-red-800"
                        title="Delete Campaign"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!loading && campaigns.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {search ? 'No campaigns match your search.' : 'No campaigns yet. Create your first campaign!'}
              </p>
            </div>
          )}
        </div>

        {/* Footer with Add Button and Pagination */}
        <div className="px-6 py-4 flex justify-between items-center border-t border-gray-200">
          <button
            onClick={() => navigate('/admin/campaigns/new')}
            className="w-12 h-12 bg-teal-800 text-white rounded-full flex items-center justify-center hover:bg-teal-700 transition-colors shadow-lg"
            title="Add New Campaign"
          >
            <Plus className="w-8 h-8" />
          </button>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="p-2 text-gray-600 hover:text-teal-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                title="Previous page"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm font-medium text-gray-700 min-w-[4rem] text-center">
                Page {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="p-2 text-gray-600 hover:text-teal-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                title="Next page"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default CampaignList;